import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
// Direct imports rather than the components barrel: this file is exported from
// that barrel, and a self-referencing import is a cycle waiting to bite.
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';

import css from './ConsentBasisFields.module.css';

/**
 * "How did these people agree to hear from you?"
 *
 * Rendered inside any form that brings contacts in by hand — a CSV upload,
 * a list import, adding an address. The values it collects become the consent
 * attestation the server requires (see server/api-util/consentBasis.js): a
 * basis from a short list, a note when the basis is "other", and an explicit
 * confirmation. Without all three the server refuses the contacts, so the
 * host form should keep its submit disabled until `attestationFromValues`
 * returns something.
 *
 * The basis list is short on purpose. A reason that cannot be picked from it
 * is a reason nobody should be relying on.
 */

export const CONSENT_BASES = ['signed_up_with_us', 'existing_customer', 'asked_in_person', 'other'];

/** The attestation payload, or null while the form is not yet complete. */
export const attestationFromValues = values => {
  const basis = values?.attestationBasis;
  const note = (values?.attestationNote || '').trim();
  const confirmed = values?.attestationConfirmed === true;
  if (!CONSENT_BASES.includes(basis) || !confirmed) return null;
  if (basis === 'other' && !note) return null;
  return { basis, note: note || null, confirmed: true };
};

const ConsentBasisFields = props => {
  const { className, values, idPrefix } = props;
  const intl = useIntl();
  const prefix = idPrefix ? `${idPrefix}-` : '';

  return (
    <div className={classNames(css.root, className)}>
      <FieldSelect
        id={`${prefix}attestationBasis`}
        name="attestationBasis"
        label={intl.formatMessage({ id: 'ConsentBasisFields.basisLabel' })}
        className={css.select}
      >
        <option value="">{intl.formatMessage({ id: 'ConsentBasisFields.basisPlaceholder' })}</option>
        {CONSENT_BASES.map(basis => (
          <option key={basis} value={basis}>
            {intl.formatMessage({ id: `ConsentBasisFields.basis.${basis}` })}
          </option>
        ))}
      </FieldSelect>

      {values?.attestationBasis === 'other' ? (
        <FieldTextInput
          id={`${prefix}attestationNote`}
          name="attestationNote"
          type="text"
          label={intl.formatMessage({ id: 'ConsentBasisFields.noteLabel' })}
          placeholder={intl.formatMessage({ id: 'ConsentBasisFields.notePlaceholder' })}
          className={css.note}
        />
      ) : null}

      <FieldCheckbox
        id={`${prefix}attestationConfirmed`}
        name="attestationConfirmed"
        label={intl.formatMessage({ id: 'ConsentBasisFields.confirmLabel' })}
        className={css.confirm}
      />

      <p className={css.hint}>
        <FormattedMessage id="ConsentBasisFields.hint" />
      </p>
    </div>
  );
};

ConsentBasisFields.defaultProps = {
  className: null,
  idPrefix: null,
  values: null,
};

ConsentBasisFields.propTypes = {
  className: string,
  idPrefix: string,
  // The host FinalForm's current values, so the note can appear only when
  // "other" is chosen.
  values: (props, propName) =>
    props[propName] == null || typeof props[propName] === 'object'
      ? null
      : new Error('values must be the form values object'),
};

export default ConsentBasisFields;
